import React, { lazy } from 'react';
import { RouteObject, useRoutes } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { links, paths } from './paths';

import { Loadable } from './atoms/loader';
import { MainLayout } from './layouts/main-layout';

import Home from './pages/home';
import Photovoltaics from './pages/photovoltaics';
import Ems from './pages/ems';
import Boiler from './pages/boiler';
import ElectricalGrid from './pages/electrical-grid';
import Statistics from './pages/statistics';
import Settings from './pages/settings';
import HeatPump from './pages/heat-pump';
import Spot from './pages/spot';
import Plug from './pages/plug';
import Relay from './pages/relay';
import EnergyMeters from './pages/energy-meters';
import Wind from './pages/wind';
import CloudPlugs from './pages/cloud-plugs';
import Reports from './pages/reports';
import ReportDetail from './pages/report-detail';

import ProductTest from './pages/product-test';
import Thermostat from './pages/thermostat';
import Protocol from './pages/protocol';
import ExpertService from './pages/expert-service';
import EmsService from './pages/ems-service';
import Wallbox from './pages/wallbox';
import CustomPage from './pages/custom-page';
import Inspect from './pages/inspect';
import InspectInverter from './pages/inspect-inverter';
import InspectWallbox from './pages/inspect-wallbox';
import InspectHeatPump from './pages/inspect-heat-pump';
import InspectSpot from './pages/inspect-spot';
import InspectMqtt from './pages/inspect-mqtt';
import InspectPlugs from './pages/inspect-plugs';
import InspectPlc from './pages/inspect-plc';
import NotFound from './pages/not-found';

const DevNetwork = Loadable(lazy(() => import('./pages/dev/network')));
import DevDeltaGreen from './pages/dev/delta-green';
import DevElectree from './pages/dev/electree';
import DevEnergomachine from './pages/dev/energomachine';
import DevMqttCloud from './pages/dev/mqtt-cloud';
import DevMqttBroker from './pages/dev/mqtt-broker';
import DevShellies from './pages/dev/shellies';
import DevAzRouters from './pages/dev/az-routers';
const DevScripts = Loadable(lazy(() => import('./pages/dev/scripts')));
import DevHomepage from './pages/dev/homepage';
import DevBezDodavatele from './pages/dev/bez-dodavatele';
import DevPreFlexibility from './pages/dev/pre-flexibility';
import DevEnerco from './pages/dev/enerco';
import DevCloudPlugs from './pages/dev/cloud-plugs';
const DevInverterConfig = Loadable(
  lazy(() => import('./pages/dev/inverter-config'))
);
import DevSubscriptions from './pages/dev/subscriptions';
import AppMessages from './pages/app-messages';
import DevAppMessages from './pages/dev/app-messages';
const DevStatusLog = Loadable(lazy(() => import('./pages/dev/status-log')));
import { RfidCards } from './pages/wallbox/rfid';
import { WallboxList } from './pages/wallbox/list';
const AuditLog = Loadable(lazy(() => import('./pages/audit-log')));

const routes: RouteObject[] = [
  {
    element: <MainLayout />,
    children: [{ path: '', element: <Home /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.photovoltaics"
            defaultMessage="Fotovoltaika"
          />
        }
        icon="solar-panel"
      />
    ),
    children: [{ path: paths.photovoltaics, element: <Photovoltaics /> }],
  },
  {
    element: (
      <MainLayout
        title={<FormattedMessage id="header.title.ems" defaultMessage="EMS" />}
        icon="solar-panel"
      />
    ),
    children: [{ path: paths.ems, element: <Ems /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage id="header.title.boiler" defaultMessage="Bojler" />
        }
        icon="boiler"
      />
    ),
    children: [{ path: paths.boiler, element: <Boiler /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.grid"
            defaultMessage="Distribuční síť"
          />
        }
        icon="electrical-grid"
      />
    ),
    children: [{ path: paths.grid, element: <ElectricalGrid /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage id="header.title.plug" defaultMessage="Zásuvka" />
        }
        icon="electrical-grid"
      />
    ),
    children: [{ path: paths.plug, element: <Plug /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage id="header.title.relay" defaultMessage="Relé" />
        }
        icon="electrical-grid"
      />
    ),
    children: [{ path: paths.relay, element: <Relay /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.energyMeters"
            defaultMessage="Elektroměry"
          />
        }
        icon="electrical-grid"
      />
    ),
    children: [{ path: paths.energyMeters, element: <EnergyMeters /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage id="header.title.spot" defaultMessage="Spot" />
        }
        icon="statistics"
      />
    ),
    children: [{ path: paths.spot, element: <Spot /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.statistics"
            defaultMessage="Statistiky"
          />
        }
        icon="statistics"
      />
    ),
    children: [{ path: paths.statistics, element: <Statistics /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.settings"
            defaultMessage="Nastavení"
          />
        }
        icon="settings"
      />
    ),
    children: [{ path: paths.settings, element: <Settings /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.heat_pump"
            defaultMessage="Tepelné čerpadlo"
          />
        }
        icon="heat-pump"
      />
    ),
    children: [{ path: paths.heatPump, element: <HeatPump /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.wind"
            defaultMessage="Větrná elektrárna"
          />
        }
        icon="wind"
      />
    ),
    children: [{ path: paths.wind, element: <Wind /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev_homepage"
            defaultMessage="Vývojové menu"
          />
        }
      />
    ),
    children: [{ path: paths.devHomepage, element: <DevHomepage /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.cloud_plugs"
            defaultMessage="Zásuvky"
          />
        }
        icon="electrical-grid"
      />
    ),
    children: [{ path: paths.cloudPlugs, element: <CloudPlugs /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.app_messages"
            defaultMessage="Zprávy"
          />
        }
      />
    ),
    children: [{ path: paths.appMessages, element: <AppMessages /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.audit_log"
            defaultMessage="Audit Log"
          />
        }
      />
    ),
    children: [{ path: paths.auditLog, element: <AuditLog /> }],
  },
  // --- dev ---
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev"
            defaultMessage="Nastavení sítě"
          />
        }
      />
    ),
    children: [{ path: paths.network, element: <DevNetwork variant="dev" /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.delta_green"
            defaultMessage="Delta Green"
          />
        }
      />
    ),
    children: [{ path: paths.deltaGreen, element: <DevDeltaGreen /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.electree"
            defaultMessage="Electree"
          />
        }
      />
    ),
    children: [{ path: paths.electree, element: <DevElectree /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.energomachine"
            defaultMessage="Energomachine"
          />
        }
      />
    ),
    children: [{ path: paths.energomachine, element: <DevEnergomachine /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.mqtt_cloud"
            defaultMessage="MQTT Cloud"
          />
        }
      />
    ),
    children: [{ path: paths.mqttCloud, element: <DevMqttCloud /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.mqtt_broker"
            defaultMessage="MQTT Broker"
          />
        }
      />
    ),
    children: [{ path: paths.mqttBroker, element: <DevMqttBroker /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.cloud_plugs"
            defaultMessage="Cloudové zásuvky"
          />
        }
      />
    ),
    children: [{ path: paths.devCloudPlugs, element: <DevCloudPlugs /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.inverter_config"
            defaultMessage="Konfigurace střídačů"
          />
        }
      />
    ),
    children: [
      { path: paths.devInverterConfig, element: <DevInverterConfig /> },
    ],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.subscriptions"
            defaultMessage="Předplatné"
          />
        }
      />
    ),
    children: [{ path: paths.devSubscriptions, element: <DevSubscriptions /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.shellies"
            defaultMessage="Shellies"
          />
        }
      />
    ),
    children: [{ path: paths.shellies, element: <DevShellies /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.az_routers"
            defaultMessage="AZ routery"
          />
        }
      />
    ),
    children: [{ path: paths.azRouters, element: <DevAzRouters /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.scripts"
            defaultMessage="Scripty"
          />
        }
      />
    ),
    children: [{ path: paths.scripts, element: <DevScripts /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.bez_dodavatele"
            defaultMessage="bezDodavatele"
          />
        }
      />
    ),
    children: [{ path: paths.bezDodavatele, element: <DevBezDodavatele /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.pre_flexibility"
            defaultMessage="PRE Flexibilita"
          />
        }
      />
    ),
    children: [{ path: paths.preFlexibility, element: <DevPreFlexibility /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.enerco"
            defaultMessage="Enerco"
          />
        }
      />
    ),
    children: [{ path: paths.enerco, element: <DevEnerco /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.app_messages"
            defaultMessage="Notifikace"
          />
        }
      />
    ),
    children: [{ path: paths.devAppMessages, element: <DevAppMessages /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.dev.status_log"
            defaultMessage="Status log"
          />
        }
      />
    ),
    children: [{ path: paths.devStatusLog, element: <DevStatusLog /> }],
  },
  // --- other ---
  {
    path: '/product-test',
    element: <ProductTest />,
  },
  {
    path: '/protokol',
    element: <Protocol />,
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.thermostat"
            defaultMessage="Termostat"
          />
        }
        icon="thermostat"
      />
    ),
    children: [{ path: paths.thermostat, element: <Thermostat /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.expert_service"
            defaultMessage="Expert Service"
          />
        }
      />
    ),
    children: [{ path: paths.expertService, element: <ExpertService /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.ems_service"
            defaultMessage="EMS Service"
          />
        }
      />
    ),
    children: [{ path: paths.emsService, element: <EmsService /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.expert_service.network"
            defaultMessage="Nastavení sítě"
          />
        }
      />
    ),
    children: [
      {
        path: paths.expertServiceNetwork,
        element: <DevNetwork variant="expert-service" />,
      },
    ],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.wallbox"
            defaultMessage="Wallbox"
          />
        }
        icon="wallbox"
      />
    ),
    children: [{ path: paths.wallbox, element: <Wallbox /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.rfid_cards"
            defaultMessage="RFID karty"
          />
        }
        variant="subpage"
        goBack={-1}
      />
    ),
    children: [{ path: paths.rfidCards, element: <RfidCards /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.wallbox_list"
            defaultMessage="Wallboxy"
          />
        }
        variant="subpage"
        goBack={-1}
      />
    ),
    children: [{ path: paths.wallboxList, element: <WallboxList /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.reports"
            defaultMessage="Reporty"
          />
        }
        icon="reports"
      />
    ),
    children: [{ path: paths.reports, element: <Reports /> }],
  },
  {
    element: (
      <MainLayout
        title={
          <FormattedMessage
            id="header.title.reports"
            defaultMessage="Reporty"
          />
        }
        icon="reports"
      />
    ),
    children: [{ path: paths.reportDetail, element: <ReportDetail /> }],
  },
  {
    element: <MainLayout />,
    children: [{ path: paths.customPage, element: <CustomPage /> }],
  },
  {
    path: '/inspect',
    element: <Inspect />,
  },
  {
    path: '/inspect/inverter',
    element: <InspectInverter />,
  },
  {
    path: '/inspect/wallbox',
    element: <InspectWallbox />,
  },
  {
    path: '/inspect/heat-pump',
    element: <InspectHeatPump />,
  },
  {
    path: '/inspect/spot',
    element: <InspectSpot />,
  },
  {
    path: '/inspect/mqtt',
    element: <InspectMqtt />,
  },
  {
    path: '/inspect/plugs',
    element: <InspectPlugs />,
  },
  {
    path: '/inspect/plc',
    element: <InspectPlc />,
  },
  {
    path: '/*',
    element: <NotFound />,
  },
];

export const Router = () => useRoutes(routes);
